import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, Package, TrendingDown, RefreshCw } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  INVENTORY FORECAST (Phase 16)
//  Consumption-based reorder suggestions + urgency ranking.
// ═══════════════════════════════════════════════════════════════════

const URGENCY_STYLE = {
  stockout:        { bg:'#fee2e2', fg:'#991b1b', label:'OUT OF STOCK', order:0 },
  critical:        { bg:'#fecaca', fg:'#7f1d1d', label:'CRITICAL',     order:1 },
  warning:         { bg:'#fef3c7', fg:'#92400e', label:'WARNING',      order:2 },
  'below-reorder': { bg:'#e0e7ff', fg:'#3730a3', label:'BELOW REORDER',order:3 },
  none:            { bg:'#f3f4f6', fg:'#374151', label:'OK',           order:4 },
};

const InventoryForecastPage = ({ API_BASE }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leadDays, setLeadDays] = useState(7);
  const [safetyDays, setSafetyDays] = useState(14);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/inventory-forecast`, {
        params: { leadDays, safetyDays },
      });
      setData(r.data);
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE, leadDays, safetyDays]);

  useEffect(() => { load(); }, [load]);

  if (!data && loading) {
    return <div style={{ padding:40, textAlign:'center', color:'#888' }}>Analyzing inventory…</div>;
  }
  if (!data) return null;

  const rows = filter === 'all' ? data.rows
    : filter === 'needs-action' ? data.rows.filter(r => r.urgency !== 'none')
    : data.rows.filter(r => r.urgency === filter);

  return (
    <div style={{ padding:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:12 }}>
        <h2 style={{ color: theme.navy, margin:0 }}>📦 Inventory Forecast</h2>
        <button onClick={load} style={{ ...btnStyle, background: theme.primary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
          <RefreshCw size={14}/> Recompute
        </button>
      </div>

      <div style={{ ...cardStyle, marginBottom:12, background: theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color: theme.navy, lineHeight:1.6 }}>
          <b>How it works:</b> Computes avg daily consumption from last 90 days (recent 30d weighted 2x). Days-remaining = current stock ÷ avg daily use. Urgency flags: <b>stockout</b> (≤0), <b>critical</b> (≤ lead time), <b>warning</b> (≤ lead+safety), <b>below-reorder</b> (below manual threshold). Cron runs daily 07:30 → WhatsApp to admin for critical/stockout.
        </div>
      </div>

      {/* Controls */}
      <div style={{ ...cardStyle, marginBottom:12, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
        <Label>Vendor lead time (days):</Label>
        <input type="number" value={leadDays} onChange={e => setLeadDays(Number(e.target.value))} style={{ ...inputStyle, width:80 }}/>
        <Label>Safety buffer (days):</Label>
        <input type="number" value={safetyDays} onChange={e => setSafetyDays(Number(e.target.value))} style={{ ...inputStyle, width:80 }}/>
      </div>

      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10, marginBottom:12 }}>
        <Stat label="Total items" value={data.counts.total} color={theme.secondary}/>
        <Stat label="Stockout" value={data.counts.stockout} color={theme.danger}/>
        <Stat label="Critical" value={data.counts.critical} color="#dc2626"/>
        <Stat label="Warning" value={data.counts.warning} color={theme.warning}/>
        <Stat label="Slow-moving" value={data.counts.slowMoving} color={theme.textMuted}/>
        <Stat label="Order value" value={`₹${data.totalSuggestedOrder.toLocaleString('en-IN')}`} color={theme.success}/>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
        {['all','needs-action','stockout','critical','warning','below-reorder','none'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding:'6px 12px', borderRadius:6, cursor:'pointer',
            border:`1px solid ${filter===f ? theme.primary : theme.border}`,
            background: filter===f ? theme.primary : 'white',
            color: filter===f ? 'white' : theme.navy, fontWeight:600, fontSize:12,
            textTransform:'capitalize',
          }}>{f === 'none' ? 'OK' : f.replace('-',' ')}</button>
        ))}
      </div>

      <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
        {rows.length === 0
          ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No items match this filter</div>
          : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f9fafb' }}>
                <th style={th}>Item</th>
                <th style={th}>Stock</th>
                <th style={th}>30d / 90d</th>
                <th style={th}>Avg/day</th>
                <th style={th}>Days left</th>
                <th style={th}>Reorder qty</th>
                <th style={th}>Order ₹</th>
                <th style={th}>Urgency</th>
              </tr></thead>
              <tbody>
                {rows.map(r => {
                  const u = URGENCY_STYLE[r.urgency] || URGENCY_STYLE.none;
                  return (
                    <tr key={r.itemId} style={{ borderBottom:'1px solid #f1f3f5' }}>
                      <td style={{ ...td, fontWeight:600 }}>
                        {r.name}
                        {r.category && <div style={{ fontSize:11, color:'#888' }}>{r.category}</div>}
                      </td>
                      <td style={td}>{r.currentStock} {r.unit || ''}</td>
                      <td style={td}><span style={{ color:'#666' }}>{r.consumed30d}</span> / {r.consumed90d}</td>
                      <td style={td}>{r.avgDailyConsumption}</td>
                      <td style={{ ...td, color: r.daysRemaining != null && r.daysRemaining <= 7 ? theme.danger : '#333', fontWeight: r.daysRemaining != null && r.daysRemaining <= 14 ? 700 : 400 }}>
                        {r.daysRemaining == null ? '∞' : `${r.daysRemaining}d`}
                      </td>
                      <td style={{ ...td, fontWeight: r.suggestedReorder > 0 ? 700 : 400, color: r.suggestedReorder > 0 ? theme.navy : '#888' }}>
                        {r.suggestedReorder > 0 ? `${r.suggestedReorder} ${r.unit || ''}` : '—'}
                      </td>
                      <td style={td}>{r.suggestedOrderValue > 0 ? `₹${r.suggestedOrderValue.toLocaleString('en-IN')}` : '—'}</td>
                      <td style={td}>
                        <span style={{ padding:'3px 8px', borderRadius:10, fontSize:'0.7rem', fontWeight:700, background: u.bg, color: u.fg }}>
                          {u.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div style={{ ...cardStyle, padding:12, borderLeft:`4px solid ${color}` }}>
    <div style={{ fontSize:10, color:'#888', textTransform:'uppercase', letterSpacing:1 }}>{label}</div>
    <div style={{ fontSize:20, fontWeight:700, color: theme.navy, marginTop:4 }}>{value}</div>
  </div>
);

const Label = ({ children }) => <span style={{ fontSize:13, color: theme.navy, fontWeight:600 }}>{children}</span>;
const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };

export default InventoryForecastPage;
